import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Button, Form } from "react-bootstrap";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import {
  closeLead,
  createLead,
  isErpApiSuccess,
  listLeads,
} from "../erpClient";
import { getDefaultBranchId } from "../utils/leadBranch";
import Pagination from "../components/Pagination";
import {
  sanitizeMobileInput,
  mobileInputFieldProps,
  isValidMobileNumber,
} from "../utils/mobileInput";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";

const STAGES = ["", "NEW", "IN_PROGRESS", "WAITING", "DONE", "LOST"];
const STATUSES = ["", "OPEN", "CLOSED"];
const LEAD_TYPES = ["", "PAYMENT", "ORDER", "GENERAL"];

function formatDt(v) {
  if (v == null || v === "") return "—";
  const s = String(v);
  return s.length > 16 ? s.slice(0, 16).replace("T", " ") : s;
}

function LeadsTable() {
  const navigate = useNavigate();
  const { buttonClass, tableClass, cardClass } = useButtonTableStyle();
  const branchId = getDefaultBranchId();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [stage, setStage] = useState("");
  const [leadType, setLeadType] = useState("");
  const [loading, setLoading] = useState(true);

  const [closeId, setCloseId] = useState(null);
  const [closeReason, setCloseReason] = useState("");
  const [closeReasonError, setCloseReasonError] = useState("");

  const [showCreate, setShowCreate] = useState(false);
  const [cName, setCName] = useState("");
  const [cMobile, setCMobile] = useState("");
  const [cTitle, setCTitle] = useState("Lead");
  const [cLeadType, setCLeadType] = useState("GENERAL");
  const [createNameError, setCreateNameError] = useState("");
  const [createMobileError, setCreateMobileError] = useState("");

  const resetCreateForm = () => {
    setCName("");
    setCMobile("");
    setCTitle("Lead");
    setCLeadType("GENERAL");
    setCreateNameError("");
    setCreateMobileError("");
  };

  const openCreateModal = () => {
    resetCreateForm();
    setShowCreate(true);
  };

  const closeCreateModal = () => {
    setShowCreate(false);
    setCreateNameError("");
    setCreateMobileError("");
  };

  const getCreateFieldErrors = () => {
    const name = cName.trim();
    const mobile = cMobile.trim();
    let nameError = "";
    let mobileError = "";
    if (!name && !mobile) {
      nameError = "Name or mobile is required";
    }
    if (mobile && !isValidMobileNumber(mobile)) {
      mobileError = "Enter a valid 10-digit mobile number";
    }
    return { nameError, mobileError };
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listLeads({
        branch_id: branchId,
        ...(status ? { status } : {}),
        ...(stage ? { stage } : {}),
        ...(leadType ? { lead_type: leadType } : {}),
        ...(search.trim() ? { search: search.trim() } : {}),
        pagination: { page, limit },
      });
      if (!isErpApiSuccess(res)) {
        setRows([]);
        setTotal(0);
        toast.error(res?.message || "Failed to load leads");
        return;
      }
      const data = res.data;
      const list = Array.isArray(data?.rows) ? data.rows : [];
      const pag = data?.pagination || {};
      setRows(list);
      setTotal(typeof pag.total === "number" ? pag.total : list.length);
    } catch (err) {
      console.error(err);
      setRows([]);
      setTotal(0);
      toast.error(
        err?.response?.data?.message || err?.message || "Failed to load leads",
      );
    } finally {
      setLoading(false);
    }
  }, [branchId, status, stage, leadType, search, page, limit]);

  useEffect(() => {
    const t = setTimeout(() => fetchList(), search ? 250 : 0);
    return () => clearTimeout(t);
  }, [fetchList, search]);

  const openClose = (id) => {
    setCloseId(id);
    setCloseReason("");
    setCloseReasonError("");
  };

  const dismissCloseModal = () => {
    setCloseId(null);
    setCloseReason("");
    setCloseReasonError("");
  };

  const submitCreate = async (e) => {
    e?.preventDefault?.();
    const { nameError, mobileError } = getCreateFieldErrors();
    if (nameError || mobileError) {
      setCreateNameError(nameError);
      setCreateMobileError(mobileError);
      return;
    }
    setCreateNameError("");
    setCreateMobileError("");
    const name = cName.trim();
    const mobile = cMobile.trim();
    try {
      const res = await createLead({
        branch_id: branchId,
        lead_type: cLeadType,
        entity_type: "NONE",
        name: name || null,
        mobile: mobile || null,
        title: cTitle.trim() || "Lead",
      });
      if (!isErpApiSuccess(res)) {
        toast.error(res?.message || "Could not create lead");
        return;
      }
      const newId = res.data?.id;
      toast.success("Lead created");
      setShowCreate(false);
      resetCreateForm();
      if (newId) {
        navigate(`/leads/${newId}`);
      } else {
        fetchList();
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Could not create lead",
      );
    }
  };

  const submitClose = async () => {
    if (!closeId) return;
    const reason = closeReason.trim();
    if (!reason) {
      setCloseReasonError("Closing reason is required");
      toast.error("Please provide a reason for closing this lead");
      return;
    }
    setCloseReasonError("");
    try {
      const res = await closeLead({
        lead_id: closeId,
        closed_reason: reason,
      });
      if (!isErpApiSuccess(res)) {
        toast.error(res?.message || "Could not close lead");
        return;
      }
      toast.success("Lead closed");
      dismissCloseModal();
      fetchList();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err?.message || "Could not close lead",
      );
    }
  };

  return (
    <>
      <div className="body-wrapper">
        <div className="container-fluid">
          <div
            className={`card w-100 position-relative overflow-hidden ${cardClass}`}
          >
            <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h4 className="card-title mb-0">Leads</h4>
              <Button
                className={`btn btn-success ${buttonClass}`}
                onClick={openCreateModal}
              >
                New lead
              </Button>
            </div>
            <div className="card-body p-4">
              <div className="d-flex flex-wrap align-items-end gap-3 mb-4">
                <Form.Group className="mb-0" style={{ minWidth: "260px" }}>
                  <Form.Label className="small text-muted mb-0">
                    Search
                  </Form.Label>
                  <Form.Control
                    className={buttonClass}
                    value={search}
                    onChange={(e) => {
                      setPage(1);
                      setSearch(e.target.value);
                    }}
                    placeholder="Name, mobile, title"
                  />
                </Form.Group>
                <Form.Group className="mb-0" style={{ minWidth: "200px" }}>
                  <Form.Label className="small text-muted mb-0">
                    Status
                  </Form.Label>
                  <Form.Select
                    className={buttonClass}
                    value={status}
                    onChange={(e) => {
                      setPage(1);
                      setStatus(e.target.value);
                    }}
                  >
                    <option value="">All</option>
                    {STATUSES.filter(Boolean).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-0" style={{ minWidth: "200px" }}>
                  <Form.Label className="small text-muted mb-0">
                    Stage
                  </Form.Label>
                  <Form.Select
                    className={buttonClass}
                    value={stage}
                    onChange={(e) => {
                      setPage(1);
                      setStage(e.target.value);
                    }}
                  >
                    <option value="">All</option>
                    {STAGES.filter(Boolean).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-0" style={{ minWidth: "200px" }}>
                  <Form.Label className="small text-muted mb-0">
                    Lead type
                  </Form.Label>
                  <Form.Select
                    className={buttonClass}
                    value={leadType}
                    onChange={(e) => {
                      setPage(1);
                      setLeadType(e.target.value);
                    }}
                  >
                    <option value="">All</option>
                    {LEAD_TYPES.filter(Boolean).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>

              <div className={`table-responsive mb-4 border ${cardClass}`}>
                <table
                  className={`table text-nowrap mb-0 align-middle ${tableClass}`}
                >
                  <thead className="text-dark fs-4">
                    <tr>
                      <th>Title</th>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Due amt</th>
                      <th>Due date</th>
                      <th>Next Follow-Up</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={7} className="text-center py-4">
                          Loading…
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center text-muted py-4">
                          No leads
                        </td>
                      </tr>
                    ) : (
                      rows.map((r) => (
                        <tr key={r.id}>
                          <td>
                            <button
                              type="button"
                              className="btn btn-link p-0 text-start"
                              onClick={() => navigate(`/leads/${r.id}`)}
                            >
                              {r.title || "—"}
                            </button>
                          </td>
                          <td>
                            <div>{r.name || "—"}</div>
                            <div className="small text-muted">
                              ({r.mobile || "—"})
                            </div>
                          </td>
                          <td>{r.status || "—"}</td>
                          <td>
                            {r.due_amount != null && r.due_amount !== ""
                              ? r.due_amount
                              : "—"}
                          </td>
                          <td>{r.due_date || "—"}</td>
                          <td>{formatDt(r.next_followup_at)}</td>
                          <td className="text-end text-nowrap">
                            {r.status === "OPEN" && (
                              <button
                                type="button"
                                className={`btn btn-primary btn-sm ${buttonClass}`}
                                onClick={() => openClose(r.id)}
                                title="Close"
                                aria-label="Close"
                              >
                                <Icon
                                  icon="solar:close-circle-bold"
                                  width="16"
                                  height="16"
                                />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {!loading && total > 0 && (
                <Pagination
                  totalItems={total}
                  itemsPerPage={limit}
                  currentPage={page}
                  onPageChange={setPage}
                  onItemsPerPageChange={(n) => {
                    setLimit(n);
                    setPage(1);
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal show={showCreate} onHide={closeCreateModal} centered>
        <Form onSubmit={submitCreate}>
          <Modal.Header closeButton>
            <Modal.Title>New lead</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted small mb-3">
              Name or mobile is required (at least one).
            </p>
            <Form.Group className="mb-2">
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={cName}
                isInvalid={!!createNameError}
                onChange={(e) => {
                  setCName(e.target.value);
                  if (createNameError) setCreateNameError("");
                }}
              />
              {createNameError ? (
                <div className="invalid-feedback d-block">{createNameError}</div>
              ) : null}
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Mobile</Form.Label>
              <Form.Control
                {...mobileInputFieldProps}
                value={cMobile}
                isInvalid={!!createMobileError}
                onChange={(e) => {
                  setCMobile(sanitizeMobileInput(e.target.value));
                  if (createMobileError) setCreateMobileError("");
                }}
              />
              {createMobileError ? (
                <div className="invalid-feedback d-block">{createMobileError}</div>
              ) : null}
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Title</Form.Label>
              <Form.Control
                value={cTitle}
                onChange={(e) => setCTitle(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label>Lead type</Form.Label>
              <Form.Select
                value={cLeadType}
                onChange={(e) => setCLeadType(e.target.value)}
              >
                <option value="GENERAL">GENERAL</option>
                <option value="PAYMENT">PAYMENT</option>
                <option value="ORDER">ORDER</option>
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeCreateModal}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!cName.trim() && !cMobile.trim()}
            >
              Create
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={closeId != null} onHide={dismissCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Close lead</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>
              Reason 
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={closeReason}
              isInvalid={!!closeReasonError}
              placeholder="Why is this lead being closed?"
              onChange={(e) => {
                setCloseReason(e.target.value);
                if (closeReasonError) setCloseReasonError("");
              }}
            />
            {closeReasonError ? (
              <div className="invalid-feedback d-block">{closeReasonError}</div>
            ) : null}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={dismissCloseModal}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={submitClose}
            disabled={!closeReason.trim()}
          >
            Close lead
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default LeadsTable;
